import React, { useState, useEffect } from 'react';
import {
  Users,
  Plus,
  Edit2,
  Trash2,
  Lock,
  Unlock,
  Search,
  Shield,
  Mail,
  Phone,
  Briefcase,
  Eye,
  EyeOff,
  Key,
  UserCheck,
  UserX,
  Filter
} from 'lucide-react';
import { useToast } from '../components/ToastContainer';
import apiService from '../services/api';
import ConfirmModal from '../components/ConfirmModal';

function UsuariosPage() {
  const toast = useToast();
  const [usuarios, setUsuarios] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [modalMode, setModalMode] = useState('crear');
  const [selectedUser, setSelectedUser] = useState(null);
  const [busqueda, setBusqueda] = useState('');
  const [filtroRol, setFiltroRol] = useState('');
  const [filtroActivo, setFiltroActivo] = useState('todos');

  const [confirmModal, setConfirmModal] = useState({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: null,
    type: 'danger'
  });

  const [formData, setFormData] = useState({
    nombre: '',
    email: '',
    password: '',
    rol: 'operador',
    telefono: '',
    departamento: '',
    activo: true
  });

  const [passwordData, setPasswordData] = useState({
    nuevaPassword: '',
    confirmarPassword: ''
  });

  const [showPassword, setShowPassword] = useState(false);
  const [showNuevaPassword, setShowNuevaPassword] = useState(false);
  const [showConfirmarPassword, setShowConfirmarPassword] = useState(false);

  useEffect(() => {
    fetchUsuarios();
    fetchRoles();
  }, [filtroRol, filtroActivo]);

  const fetchUsuarios = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();

      if (filtroRol) params.append('rol', filtroRol);
      if (filtroActivo !== 'todos') params.append('activo', filtroActivo === 'activos');
      if (busqueda) params.append('busqueda', busqueda);

      const data = await apiService.get(`/usuarios?${params}`);
      setUsuarios(data);
    } catch (error) {
      console.error('Error al cargar usuarios:', error);
      if (error.message !== 'Sesión expirada. Por favor, vuelve a iniciar sesión.') {
        toast.error('Error al cargar usuarios');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchRoles = async () => {
    try {
      const data = await apiService.get('/roles');
      setRoles(data);
    } catch (error) {
      console.error('Error al cargar roles:', error);
    }
  };

  const handleOpenModal = (mode, user = null) => {
    setModalMode(mode);
    setSelectedUser(user);
    
    if (mode === 'editar' && user) {
      setFormData({
        nombre: user.nombre,
        email: user.email,
        password: '',
        rol: user.rol,
        telefono: user.telefono || '',
        departamento: user.departamento || '',
        activo: user.activo
      });
    } else {
      setFormData({
        nombre: '',
        email: '',
        password: '',
        rol: 'operador',
        telefono: '',
        departamento: '',
        activo: true
      });
    }
    
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedUser(null);
    setFormData({
      nombre: '',
      email: '',
      password: '',
      rol: 'operador',
      telefono: '',
      departamento: '',
      activo: true
    });
  };

  const handleClosePasswordModal = () => {
    setShowPasswordModal(false);
    setSelectedUser(null);
    setPasswordData({ nuevaPassword: '', confirmarPassword: '' });
    setShowNuevaPassword(false);
    setShowConfirmarPassword(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.nombre || !formData.email) {
      toast.error('Nombre y email son requeridos');
      return;
    }

    if (modalMode === 'crear' && !formData.password) {
      toast.error('La contraseña es requerida');
      return;
    }

    if (modalMode === 'crear' && formData.password.length < 6) {
      toast.error('La contraseña debe tener al menos 6 caracteres');
      return;
    }

    try {
      const dataToSend = modalMode === 'crear'
        ? formData
        : { ...formData, password: undefined };

      const endpoint = modalMode === 'crear'
        ? '/usuarios'
        : `/usuarios/${selectedUser.id}`;

      const method = modalMode === 'crear' ? 'post' : 'put';

      await apiService[method](endpoint, dataToSend);

      toast.success(
        modalMode === 'crear'
          ? `Usuario "${formData.nombre}" creado exitosamente`
          : `Usuario "${formData.nombre}" actualizado correctamente`
      );
      handleCloseModal();
      fetchUsuarios();
    } catch (error) {
      console.error('Error:', error);
      if (error.message !== 'Sesión expirada. Por favor, vuelve a iniciar sesión.') {
        toast.error(error.message || 'Error de conexión con el servidor');
      }
    }
  };

  const handleDelete = async (id, nombre) => {
    setConfirmModal({
      isOpen: true,
      title: '¿Eliminar Usuario?',
      message: `Estás a punto de eliminar al usuario "${nombre}". Esta acción no se puede deshacer.`,
      type: 'danger',
      onConfirm: async () => {
        setConfirmModal(prev => ({ ...prev, isOpen: false }));
        try {
          await apiService.delete(`/usuarios/${id}`);
          toast.success(`🗑️ Usuario "${nombre}" eliminado correctamente`);
          fetchUsuarios();
        } catch (error) {
          console.error('Error:', error);
          if (error.message !== 'Sesión expirada. Por favor, vuelve a iniciar sesión.') {
            toast.error(error.message || 'Error al eliminar usuario');
          }
        }
      }
    });
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();

    if (passwordData.nuevaPassword !== passwordData.confirmarPassword) {
      toast.warning('Las contraseñas no coinciden');
      return;
    }

    if (passwordData.nuevaPassword.length < 6) {
      toast.error('La contraseña debe tener al menos 6 caracteres');
      return;
    }

    try {
      await apiService.post(`/usuarios/${selectedUser.id}/reset-password`, {
        nuevaPassword: passwordData.nuevaPassword
      });

      toast.success(`🔑 Contraseña de "${selectedUser.nombre}" reseteada exitosamente`);
      setShowPasswordModal(false);
      setSelectedUser(null);
      setPasswordData({ nuevaPassword: '', confirmarPassword: '' });
    } catch (error) {
      console.error('Error:', error);
      if (error.message !== 'Sesión expirada. Por favor, vuelve a iniciar sesión.') {
        toast.error(error.message || 'Error al resetear contraseña');
      }
    }
  };

  const handleDesbloquear = async (id, nombre) => {
    setConfirmModal({
      isOpen: true,
      title: '¿Desbloquear Usuario?',
      message: `¿Estás seguro de desbloquear al usuario "${nombre}"?`,
      type: 'warning',
      onConfirm: async () => {
        setConfirmModal(prev => ({ ...prev, isOpen: false }));
        try {
          await apiService.post(`/usuarios/${id}/desbloquear`, {});
          toast.success(`Usuario "${nombre}" desbloqueado correctamente`);
          fetchUsuarios();
        } catch (error) {
          console.error('Error:', error);
          if (error.message !== 'Sesión expirada. Por favor, vuelve a iniciar sesión.') {
            toast.error(error.message || 'Error al desbloquear usuario');
          }
        }
      }
    });
  };

  const getRolColor = (rol) => {
    switch (rol) {
      case 'superadmin': return 'bg-red-100 text-red-800 border-red-300';
      case 'admin': return 'bg-orange-100 text-orange-800 border-orange-300';
      case 'manager': return 'bg-blue-100 text-blue-800 border-blue-300';
      case 'operador': return 'bg-green-100 text-green-800 border-green-300';
      case 'finanzas': return 'bg-purple-100 text-purple-800 border-purple-300';
      case 'consultor': return 'bg-gray-100 text-gray-800 border-gray-300';
      case 'cliente': return 'bg-teal-100 text-teal-800 border-teal-300';
      default: return 'bg-gray-100 text-gray-800 border-gray-300';
    }
  };

  const getRolIcon = (rol) => {
    return <Shield size={14} />;
  };

  const usuariosFiltrados = usuarios.filter(u => {
    const matchBusqueda = !busqueda || 
      u.nombre.toLowerCase().includes(busqueda.toLowerCase()) ||
      u.email.toLowerCase().includes(busqueda.toLowerCase());
    
    return matchBusqueda;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-ink-50">
        <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-ink-900"></div>
      </div>
    );
  }

  return (
    <div className="p-8 bg-ink-50 min-h-screen">
      {/* Header */}
      <div className="mb-8 animate-fade-in">
        <h1 className="font-display text-3xl text-ink-900 mb-1 flex items-center gap-3">
          <Users size={32} className="text-ink-700" />
          Gestión de Usuarios
        </h1>
        <p className="text-ink-400">Administra usuarios, roles y permisos del sistema</p>
      </div>

      {/* Estadísticas */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
        <div className="bg-white border-l-4 border-ink-900 shadow-soft rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <Users size={32} className="text-ink-700" />
          </div>
          <p className="text-ink-500 text-sm font-medium">Total Usuarios</p>
          <p className="text-3xl font-bold text-ink-900 mt-1">{usuarios.length}</p>
        </div>

        <div className="bg-white rounded-lg p-6 shadow-soft border-l-4 border-green-500">
          <div className="flex items-center gap-3 mb-2">
            <UserCheck className="text-green-600" size={24} />
            <p className="text-ink-700 font-semibold">Activos</p>
          </div>
          <p className="text-3xl font-bold text-ink-900">
            {usuarios.filter(u => u.activo).length}
          </p>
        </div>

        <div className="bg-white rounded-lg p-6 shadow-soft border-l-4 border-red-500">
          <div className="flex items-center gap-3 mb-2">
            <UserX className="text-red-600" size={24} />
            <p className="text-ink-700 font-semibold">Inactivos</p>
          </div>
          <p className="text-3xl font-bold text-ink-900">
            {usuarios.filter(u => !u.activo).length}
          </p>
        </div>

        <div className="bg-white rounded-lg p-6 shadow-soft border-l-4 border-ink-900">
          <div className="flex items-center gap-3 mb-2">
            <Shield className="text-ink-700" size={24} />
            <p className="text-ink-700 font-semibold">Roles</p>
          </div>
          <p className="text-3xl font-bold text-ink-900">{roles.length}</p>
        </div>
      </div>

      {/* Filtros y búsqueda */}
      <div className="bg-white rounded-lg shadow-soft border border-ink-200 p-6 mb-6">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-4 flex-1">
            <div className="relative flex-1 max-w-md">
              <Search size={20} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-ink-400" />
              <input
                type="text"
                placeholder="Buscar por nombre o email..."
                value={busqueda}
                onChange={(e) => setBusqueda(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-ink-300 rounded-lg focus:ring-2 focus:ring-ink-500"
              />
            </div>

            <select
              value={filtroRol}
              onChange={(e) => setFiltroRol(e.target.value)}
              className="px-4 py-2 border border-ink-300 rounded-lg focus:ring-2 focus:ring-ink-500"
            >
              <option value="">Todos los roles</option>
              {roles.map(rol => (
                <option key={rol.id} value={rol.nombre}>{rol.nombre}</option>
              ))}
            </select>

            <select
              value={filtroActivo}
              onChange={(e) => setFiltroActivo(e.target.value)}
              className="px-4 py-2 border border-ink-300 rounded-lg focus:ring-2 focus:ring-ink-500"
            >
              <option value="todos">Todos</option>
              <option value="activos">Activos</option>
              <option value="inactivos">Inactivos</option>
            </select>
          </div>

          <button
            onClick={() => handleOpenModal('crear')}
            className="px-6 py-2 bg-ink-900 text-white rounded-lg hover:bg-ink-800 transition flex items-center gap-2 shadow-soft"
          >
            <Plus size={20} />
            Nuevo Usuario
          </button>
        </div>
      </div>

      {/* Tabla de usuarios */}
      <div className="bg-white rounded-lg shadow-soft border border-ink-200 overflow-hidden">
        {usuariosFiltrados.length === 0 ? (
          <div className="text-center py-16">
            <Users size={64} className="text-ink-300 mx-auto mb-4" />
            <p className="text-xl font-semibold text-ink-900">No hay usuarios</p>
            <p className="text-ink-500 mt-2">
              {busqueda ? 'No se encontraron resultados' : 'Crea tu primer usuario'}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-ink-50 border-b border-ink-200">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-medium text-ink-700 uppercase">Usuario</th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-ink-700 uppercase">Rol</th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-ink-700 uppercase">Contacto</th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-ink-700 uppercase">Estado</th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-ink-700 uppercase">Último acceso</th>
                  <th className="px-6 py-4 text-right text-xs font-medium text-ink-700 uppercase">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-ink-200">
                {usuariosFiltrados.map((usuario) => (
                  <tr key={usuario.id} className="hover:bg-ink-50 transition">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-ink-100 rounded-full flex items-center justify-center">
                          <span className="text-ink-700 font-bold text-sm">
                            {usuario.nombre.charAt(0).toUpperCase()}
                          </span>
                        </div>
                        <div>
                          <p className="font-semibold text-ink-900">{usuario.nombre}</p>
                          <p className="text-sm text-ink-500 flex items-center gap-1">
                            <Mail size={12} />
                            {usuario.email}
                          </p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold border ${getRolColor(usuario.rol)}`}>
                        {getRolIcon(usuario.rol)}
                        {usuario.rol}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm">
                        {usuario.telefono && (
                          <p className="text-ink-700 flex items-center gap-1 mb-1">
                            <Phone size={12} />
                            {usuario.telefono}
                          </p>
                        )}
                        {usuario.departamento && (
                          <p className="text-ink-500 flex items-center gap-1">
                            <Briefcase size={12} />
                            {usuario.departamento}
                          </p>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold ${
                        usuario.activo
                          ? 'bg-green-100 text-green-800'
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {usuario.activo ? '✓ Activo' : '✗ Inactivo'}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-ink-600">
                      {usuario.ultimo_acceso
                        ? new Date(usuario.ultimo_acceso).toLocaleString('es-MX')
                        : 'Nunca'
                      }
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => handleOpenModal('editar', usuario)}
                          className="p-2 text-ink-700 hover:bg-ink-50 rounded-lg transition"
                          title="Editar"
                        >
                          <Edit2 size={18} />
                        </button>
                        <button
                          onClick={() => {
                            setSelectedUser(usuario);
                            setShowPasswordModal(true);
                          }}
                          className="p-2 text-ink-700 hover:bg-ink-50 rounded-lg transition"
                          title="Resetear contraseña"
                        >
                          <Key size={18} />
                        </button>
                        {usuario.bloqueado_hasta && (
                          <button
                            onClick={() => handleDesbloquear(usuario.id, usuario.nombre)}
                            className="p-2 text-orange-600 hover:bg-orange-50 rounded-lg transition"
                            title="Desbloquear"
                          >
                            <Unlock size={18} />
                          </button>
                        )}
                        <button
                          onClick={() => handleDelete(usuario.id, usuario.nombre)}
                          className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition"
                          title="Eliminar"
                        >
                          <Trash2 size={18} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal crear/editar usuario */}
      {showModal && (
        <div className="fixed inset-0 bg-ink-900 bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl shadow-lifted w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="bg-ink-900 p-6 text-white rounded-t-2xl">
              <h2 className="text-2xl font-bold">
                {modalMode === 'crear' ? 'Nuevo Usuario' : 'Editar Usuario'}
              </h2>
              <p className="text-ink-300 mt-1">
                {modalMode === 'crear'
                  ? 'Complete los datos para crear un nuevo usuario'
                  : 'Modifique los datos del usuario'
                }
              </p>
            </div>

            <form onSubmit={handleSubmit} className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-ink-700 mb-2">
                    Nombre completo *
                  </label>
                  <input
                    type="text"
                    value={formData.nombre}
                    onChange={(e) => setFormData({...formData, nombre: e.target.value})}
                    className="w-full px-4 py-2 border border-ink-300 rounded-lg focus:ring-2 focus:ring-ink-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-ink-700 mb-2">
                    Email *
                  </label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    className="w-full px-4 py-2 border border-ink-300 rounded-lg focus:ring-2 focus:ring-ink-500"
                    required
                  />
                </div>

                {modalMode === 'crear' && (
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Contraseña * (mínimo 6 caracteres)
                    </label>
                    <div className="relative">
                      <input
                        type={showPassword ? 'text' : 'password'}
                        value={formData.password}
                        onChange={(e) => setFormData({...formData, password: e.target.value})}
                        className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                        required={modalMode === 'crear'}
                        minLength={6}
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500"
                      >
                        {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                      </button>
                    </div>
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Rol *
                  </label>
                  <select
                    value={formData.rol}
                    onChange={(e) => setFormData({...formData, rol: e.target.value})}
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                    required
                  >
                    {roles.map(rol => (
                      <option key={rol.id} value={rol.nombre}>
                        {rol.nombre} - {rol.descripcion}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Teléfono
                  </label>
                  <input
                    type="tel"
                    value={formData.telefono}
                    onChange={(e) => setFormData({...formData, telefono: e.target.value})}
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Departamento
                  </label>
                  <input
                    type="text"
                    value={formData.departamento}
                    onChange={(e) => setFormData({...formData, departamento: e.target.value})}
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="Ej: Operaciones, Finanzas, etc."
                  />
                </div>

                {modalMode === 'editar' && (
                  <div className="md:col-span-2">
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={formData.activo}
                        onChange={(e) => setFormData({...formData, activo: e.target.checked})}
                        className="w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                      />
                      <span className="text-sm font-medium text-gray-700">Usuario activo</span>
                    </label>
                  </div>
                )}
              </div>

              <div className="flex justify-end gap-4 mt-8">
                <button
                  type="button"
                  onClick={handleCloseModal}
                  className="px-6 py-2 border border-ink-300 text-ink-700 rounded-lg hover:bg-ink-50 transition"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="px-6 py-2 bg-ink-900 text-white rounded-lg hover:bg-ink-800 transition"
                >
                  {modalMode === 'crear' ? 'Crear Usuario' : 'Guardar Cambios'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal resetear contraseña */}
      {showPasswordModal && selectedUser && (
        <div className="fixed inset-0 bg-ink-900 bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl shadow-lifted w-full max-w-md">
            <div className="bg-ink-900 p-6 text-white rounded-t-2xl">
              <h2 className="text-2xl font-bold flex items-center gap-2">
                <Key size={24} />
                Resetear Contraseña
              </h2>
              <p className="text-ink-300 mt-1">
                Usuario: {selectedUser.nombre}
              </p>
            </div>

            <form onSubmit={handleResetPassword} className="p-6">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-ink-700 mb-2">
                    Nueva Contraseña *
                  </label>
                  <div className="relative">
                    <input
                      type={showNuevaPassword ? 'text' : 'password'}
                      value={passwordData.nuevaPassword}
                      onChange={(e) => setPasswordData({...passwordData, nuevaPassword: e.target.value})}
                      className="w-full px-4 py-2 pr-10 border border-ink-300 rounded-lg focus:ring-2 focus:ring-ink-500"
                      required
                      minLength={6}
                    />
                    <button
                      type="button"
                      onClick={() => setShowNuevaPassword(!showNuevaPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-ink-500 hover:text-ink-700"
                    >
                      {showNuevaPassword ? <Eye size={20} /> : <EyeOff size={20} />}
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-ink-700 mb-2">
                    Confirmar Contraseña *
                  </label>
                  <div className="relative">
                    <input
                      type={showConfirmarPassword ? 'text' : 'password'}
                      value={passwordData.confirmarPassword}
                      onChange={(e) => setPasswordData({...passwordData, confirmarPassword: e.target.value})}
                      className="w-full px-4 py-2 pr-10 border border-ink-300 rounded-lg focus:ring-2 focus:ring-ink-500"
                      required
                      minLength={6}
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmarPassword(!showConfirmarPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-ink-500 hover:text-ink-700"
                    >
                      {showConfirmarPassword ? <Eye size={20} /> : <EyeOff size={20} />}
                    </button>
                  </div>
                </div>
              </div>

              <div className="flex justify-end gap-4 mt-6">
                <button
                  type="button"
                  onClick={handleClosePasswordModal}
                  className="px-6 py-2 border border-ink-300 text-ink-700 rounded-lg hover:bg-ink-50 transition"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="px-6 py-2 bg-ink-900 text-white rounded-lg hover:bg-ink-800 transition"
                >
                  Resetear Contraseña
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal de Confirmación */}
      <ConfirmModal
        isOpen={confirmModal.isOpen}
        onClose={() => setConfirmModal({ ...confirmModal, isOpen: false })}
        onConfirm={confirmModal.onConfirm}
        title={confirmModal.title}
        message={confirmModal.message}
        type={confirmModal.type}
        confirmText="Sí, continuar"
        cancelText="Cancelar"
      />
    </div>
  );
}

export default UsuariosPage;

      
        